// Phones (< 768 px): the modes in a bottom bar (design spec §2 Mobile), 56 px plus the safe
// area, up to four slots, 11 px labels. The rail hides at the same width.
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type { LucideIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "../cn";
import { CountPill } from "./count-pill";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./dropdown-menu";
import { FOCUS_RING_INSET } from "./focus-ring";

function BottomBar({
  children,
  className,
  "aria-label": label = "Mode bar",
}: {
  children: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <nav
      aria-label={label}
      data-slot="mobile-mode-bar"
      className={cn(
        "fixed inset-x-0 bottom-0 z-topbar border-t border-border bg-card pb-[env(safe-area-inset-bottom)] md:hidden",
        className,
      )}
    >
      <ul className="grid auto-cols-fr grid-flow-col">{children}</ul>
    </nav>
  );
}

function BottomBarItem({
  icon: Icon,
  label,
  active,
  count,
  dot,
  className,
  render,
  ...props
}: useRender.ComponentProps<"a"> & {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  /** An unread count on the icon (the Inbox). */
  count?: number | undefined;
  /** A small accent dot on the icon (Live: an agent screen is running). */
  dot?: boolean | undefined;
}) {
  const item = useRender({
    defaultTagName: "a",
    render,
    props: mergeProps<"a">(
      {
        "aria-current": active ? "page" : undefined,
        className: cn(
          `flex h-14 flex-col items-center justify-center gap-0.5 text-2xs font-medium text-muted-foreground aria-[current=page]:text-primary-text ${FOCUS_RING_INSET}`,
          className,
        ),
        children: (
          <>
            <span className="relative">
              <Icon aria-hidden="true" className="size-5" />
              {count && count > 0 ? (
                <CountPill count={count} tone="accent" className="absolute -top-1.5 left-3" />
              ) : dot ? (
                <span
                  aria-hidden="true"
                  data-slot="bottom-bar-dot"
                  className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-success ring-2 ring-card"
                />
              ) : null}
            </span>
            {label}
          </>
        ),
      },
      props,
    ),
    state: { slot: "bottom-bar-item" },
  });
  return <li data-slot="bottom-bar-item">{item}</li>;
}

/**
 * The bar's last slot when there are more modes than slots: a "More" button opening a menu of the
 * rest (`DropdownMenuItem`s as children), so every mode stays one tap away on a phone. It reads
 * as current while one of its modes is open.
 */
function BottomBarMenu({
  icon: Icon,
  label,
  active,
  children,
  "aria-label": ariaLabel,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  children: React.ReactNode;
  /** The button's name when the label alone is too short (Me: "Account: <name>"). */
  "aria-label"?: string;
}) {
  return (
    <li data-slot="bottom-bar-item">
      <DropdownMenu>
        <DropdownMenuTrigger
          data-slot="bottom-bar-menu"
          aria-label={ariaLabel}
          aria-current={active ? "page" : undefined}
          className={`flex h-14 w-full flex-col items-center justify-center gap-0.5 text-2xs font-medium text-muted-foreground aria-[current=page]:text-primary-text ${FOCUS_RING_INSET}`}
        >
          <Icon aria-hidden="true" className="size-5" />
          {label}
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end" className="w-56">
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

export { BottomBar, BottomBarItem, BottomBarMenu };
